import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';

type AuthMode = 'magic_link' | 'password' | 'forgot_password';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.page.html',
  styleUrl: './login.page.css'
})
export class LoginPage implements OnInit {
  email = '';
  password = '';
  mode: AuthMode = 'magic_link';
  submitted = false;
  forgotPasswordSent = false;
  loading = false;
  errorMessage = '';
  sessionExpiredMessage = '';
  showPassword = false;
  returnUrl = '/dashboard/home';

  private monecoEmailPattern = /^[a-zA-Z0-9._%+-]+@moneco\.app$/;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    const sessionExpired = this.route.snapshot.queryParamMap.get('sessionExpired');
    if (sessionExpired === 'true') {
      this.sessionExpiredMessage = 'Your session has expired. Please sign in again to continue.';
    }

    const rawReturnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.returnUrl = this.sanitizeReturnUrl(rawReturnUrl);
  }

  private sanitizeReturnUrl(url: string | null): string {
    if (!url) return '/dashboard/home';
    if (url.startsWith('/dashboard')) {
      return url;
    }
    return '/dashboard/home';
  }

  setMode(mode: AuthMode) {
    this.mode = mode;
    this.errorMessage = '';
    this.submitted = false;
    this.forgotPasswordSent = false;
    this.password = '';
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.errorMessage = 'Invalid email address.';
      return false;
    }

    if (!this.monecoEmailPattern.test(email)) {
      this.errorMessage = 'Only @moneco.app email addresses are allowed.';
      return false;
    }

    return true;
  }

  submitMagicLink() {
    this.errorMessage = '';
    if (!this.validateEmail(this.email)) {
      return;
    }

    this.loading = true;
    this.authService.login(this.email);
    this.loading = false;
    this.submitted = true;
  }

  async submitPassword() {
    this.errorMessage = '';
    if (!this.validateEmail(this.email)) {
      return;
    }

    if (!this.password) {
      this.errorMessage = 'Password is required.';
      return;
    }

    this.loading = true;
    try {
      const res = await this.authService.loginWithPassword(this.email, this.password);
      const isEnrolled = !!(res.member?.totp_registration_id || res.member?.mfa_enrolled);
      const mfaRequired = !!(res.mfa_required || res.intermediate_session_token);

      const mfaQueryParams = this.returnUrl ? { returnUrl: this.returnUrl } : undefined;

      if (!isEnrolled || mfaRequired) {
        this.router.navigate(['/auth/mfa'], { queryParams: mfaQueryParams });
      } else if (this.authService.isLoggedIn()) {
        this.router.navigateByUrl(this.returnUrl);
      } else {
        this.router.navigate(['/auth/mfa'], { queryParams: mfaQueryParams });
      }
    } catch (err: any) {
      console.error('Password authentication error:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Invalid email or password. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  async submitForgotPassword() {
    this.errorMessage = '';
    if (!this.validateEmail(this.email)) {
      return;
    }

    this.loading = true;
    try {
      await this.authService.forgotPassword(this.email);
      this.forgotPasswordSent = true;
    } catch (err: any) {
      console.error('Forgot password error:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Failed to send password reset email. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}

